import type { Doc } from "building-types/doc";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { OptionOverrides, PaletteOptionSetWithId } from "building-types/options/optionTemplateTypes";
import type { RWTable, Table } from "common/data/table";
import deepmerge, { type MergeOptions } from "common/utils/deepmerge";
import { deleteIn } from "common/utils/objectUtils";
import { optionSetDefaultOptionId } from "./optionSetBaseUtils";
import { isPaletteOptionSet } from "./paletteOptionSet";

export function paletteOptionSetWithId(optionSets: Doc["optionSets"]): PaletteOptionSetWithId | undefined {
  for (const optionSetId in optionSets) {
    if (!isPaletteOptionSet(optionSets[optionSetId])) {
      continue;
    }
    const palette = optionSets[optionSetId];

    return { palette, optionSetId };
  }
  return undefined;
}

// Used when system option sets (e.g. elevationStyle) need to be modified with user overrides
// TODO: This should not live here (maybe keep in systemOptionSet)
export function mergeOverridesIntoSystemOptionSet(
  optionIds: string[],
  target: DocOptionSet,
  optionSets: Table<DocOptionSet>,
  source?: DocOptionSet,
  optionsToMerge?: MergeOptions,
) {
  if (source) {
    target = deepmerge(target, source, optionsToMerge);
  }

  // remove defaultValues that are no longer valid
  const defaultValue = optionSetDefaultOptionId(target, optionSets);
  if (defaultValue && !optionIds.includes(defaultValue)) {
    target = deleteIn(target, ["defaultValue"]);
  }

  // remove options that are no longer valid
  if (target.options) {
    for (const optionId in target.options) {
      if (!optionIds.includes(optionId)) {
        target = deleteIn(target, ["options", optionId]);
      }
    }
  }

  return target;
}

// Merge options with overrides
export function mergeOptionsOverridesIntoOptions(
  options: RWTable<DocOption>,
  optionsOverrides?: RWTable<OptionOverrides | undefined>,
) {
  const newOptions = {
    ...options,
  };
  if (!optionsOverrides) {
    return newOptions;
  }

  for (const optionId in optionsOverrides) {
    // Only merge if the option exists
    if (options[optionId]) {
      newOptions[optionId] = mergeOptionOverridesIntoOption(options[optionId], optionsOverrides[optionId]);
    }
  }
  return newOptions;
}

// Merge a single option with overrides
export function mergeOptionOverridesIntoOption(option: DocOption, optionOverrides?: OptionOverrides): DocOption {
  if (!optionOverrides) {
    return option;
  }
  // Note: this is only shallow merge. This is as intended: salesImages and formula should be overwritten not merged.
  // Because these are the only objects on DocOption, the default behavior is now set to shallow merge.
  // If another object is added to DocOption and it is desired to deep merge that object, it should be handled independently.
  return {
    ...option,
    ...optionOverrides,
  };
}
